import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import { useReturnOrder, useReturnOrderStore } from "hooks";
import type {
  ReturnOrderItem,
  ReturnOrderQuery,
  ReturnOrderRow,
  Unit,
  WmsCode,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "utils";
import { sanitizeReturnOrderItems } from "utils/helpers/sanitizers";
import {
  compareDateFilter,
  formatDateSafe,
  normalizeDateInput,
} from "utils/helpers/accounting";
import { accountCodeService, wmsCodeService } from "services";

const ReturnOrders: React.FC = () => {
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useState<ReturnOrderQuery>({
    page: 1,
    pageSize: 20,
  });
  const [confirmRow, setConfirmRow] = useState<ReturnOrderRow | null>(null);
  const [selectedItems, setSelectedItems] = useState<ReturnOrderItem[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string>("");
  const [wmsCodes, setWmsCodes] = useState<WmsCode[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);

  const {
    rows,
    setRows,
    removeRow,
    isLoading,
    setLoading,
  } = useReturnOrderStore();

  const { fetchList, deleteReturnOrder } = useReturnOrder();

  const {
    data: returnOrderData,
    isFetching,
  } = fetchList(queryParams, true);

  const parseNumber = useCallback((value: unknown) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const parsed = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const parseBoolean = useCallback((value: unknown) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      const lowered = value.toLowerCase();
      if (["true", "1", "yes", "on"].includes(lowered)) return true;
      if (["false", "0", "no", "off"].includes(lowered)) return false;
    }
    return Boolean(value);
  }, []);

  const normalizeAttachments = useCallback((value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === "string" ? item : String(item ?? "")))
        .map((item) => item.trim())
        .filter(Boolean);
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return [];

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) =>
              typeof item === "string" ? item : String(item ?? ""),
            )
            .map((item) => item.trim())
            .filter(Boolean);
        }
      } catch (error) {
        // ignore invalid JSON strings
      }

      return trimmed
        .split(/[,\n]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  }, []);

  const sanitizeItems = useCallback(
    (items?: ReturnOrderRow["items"]) => sanitizeReturnOrderItems(items),
    [],
  );

  const withItemFields = useCallback(
    (order: ReturnOrderRow): ReturnOrderRow => {
      const normalizedItems = sanitizeItems(order.items);
      const totalReturnAmount = normalizedItems.reduce(
        (sum, item) =>
          sum +
          (parseNumber(
            item.return_amount ??
              item.convert_return_price ??
              item.sale_amount ??
              0,
          ) || 0),
        0,
      );

      return {
        ...order,
        attachments: normalizeAttachments(order.attachments),
        return_date: normalizeDateInput(order.return_date) || order.return_date,
        due_date: normalizeDateInput(order.due_date) || order.due_date,
        refund_now: parseBoolean(order.refund_now),
        no_refund: parseBoolean(order.no_refund),
        items: normalizedItems,
        total_return_amount: totalReturnAmount || undefined,
      };
    },
    [normalizeAttachments, parseBoolean, parseNumber, sanitizeItems],
  );

  useEffect(() => {
    if (returnOrderData?.data) {
      const mapped =
        (returnOrderData.data as ReturnOrderRow[])?.map((item) =>
          withItemFields(item),
        ) ?? [];
      setRows(mapped);
      if (mapped.length) {
        setSelectedItems(mapped[0].items ?? []);
        setSelectedLabel(
          mapped[0].sale_order_code ??
            mapped[0].customer_name ??
            `Phiếu ${mapped[0].id ?? ""}`,
        );
      } else {
        setSelectedItems([]);
        setSelectedLabel("");
      }
    }
  }, [returnOrderData, setRows, withItemFields]);

  useEffect(() => {
    let mounted = true;
    wmsCodeService
      .list()
      .then((codes) => {
        if (!mounted) return;
        setWmsCodes(codes ?? []);
      })
      .catch(() => {
        if (!mounted) return;
        setWmsCodes([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    accountCodeService
      .list({ page: 1, pageSize: 500, type: "unit" })
      .then((res) => {
        if (!mounted) return;
        const list =
          res?.data && Array.isArray(res.data) ? (res.data as Unit[]) : [];
        setUnits(list);
      })
      .catch(() => {
        if (!mounted) return;
        setUnits([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setLoading(Boolean(isFetching || deleteReturnOrder.isPending));
  }, [deleteReturnOrder.isPending, isFetching, setLoading]);

  const columns = useMemo<ColDef<ReturnOrderRow>[]>(() => {
    return [
      {
        headerName: "Ngày nhập lại",
        field: "return_date",
        minWidth: 140,
        filter: "agDateColumnFilter",
        filterParams: { comparator: compareDateFilter },
        valueFormatter: (params) => formatDateSafe(params.value),
      },
      {
        headerName: "Hạn thanh toán",
        field: "due_date",
        minWidth: 140,
        filter: "agDateColumnFilter",
        filterParams: { comparator: compareDateFilter },
        valueFormatter: (params) => formatDateSafe(params.value),
      },
      {
        headerName: "Mã đơn bán",
        field: "sale_order_code",
        minWidth: 160,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Khách hàng",
        field: "customer_name",
        minWidth: 200,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Người nhận",
        field: "receiver_name",
        minWidth: 180,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "PT thanh toán",
        field: "payment_method",
        minWidth: 160,
        filter: "agTextColumnFilter",
        valueFormatter: (params) => {
          const val = params.value;
          if (val === "cash") return "Tiền mặt";
          if (val === "bank") return "Ngân hàng";
          if (val === "debt") return "Công nợ";
          return val ?? "";
        },
      },
      {
        headerName: "Hoàn tiền ngay",
        field: "refund_now",
        minWidth: 140,
        cellDataType: "text",
        filter: "agTextColumnFilter",
        valueFormatter: (params) => (params.value ? "Có" : "Không"),
      },
      {
        headerName: "Không hoàn tiền",
        field: "no_refund",
        minWidth: 150,
        cellDataType: "text",
        filter: "agTextColumnFilter",
        valueFormatter: (params) => (params.value ? "Có" : "Không"),
      },
      {
        headerName: "Mô tả",
        field: "description",
        minWidth: 200,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      // {
      //   headerName: "Tệp đính kèm",
      //   field: "attachments",
      //   minWidth: 180,
      //   filter: "agTextColumnFilter",
      //   valueFormatter: (params) => {
      //     if (Array.isArray(params.value)) {
      //       return params.value.join(", ");
      //     }
      //     return params.value || "";
      //   },
      // },
      {
        headerName: "Tổng tiền trả",
        field: "total_return_amount",
        minWidth: 160,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          parseNumber(params.value ?? 0).toLocaleString("vi-VN"),
      },
    ];
  }, [parseNumber]);

  const handleEditRow = useCallback(
    (params) => {
      const id = params?.data?.id ?? params?.id;
      if (id) {
        navigate(`${ROUTES.ACCOUNTING.PURCHASE_RETURN_DETAIL}/${id}`);
      } else {
        SnackbarUtils.error("Không tìm thấy chứng từ để chỉnh sửa");
      }
    },
    [navigate],
  );

  const handleRowClick = useCallback(
    (event: RowClickedEvent<ReturnOrderRow>) => {
      const clickTarget = (event.event?.target as HTMLElement) || null;
      if (clickTarget?.closest("button, [role='button'], a")) return;
      const items = sanitizeItems(event.data?.items);
      setSelectedItems(items);
      setSelectedLabel(
        event.data?.sale_order_code ??
          event.data?.customer_name ??
          `Phiếu ${event.data?.id ?? ""}`,
      );
    },
    [sanitizeItems],
  );

  const handleDeleteRow = useCallback((row: ReturnOrderRow) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteReturnOrder.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa chứng từ không thành công");
      },
    });
  }, [confirmRow, deleteReturnOrder, removeRow]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      editable: false,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-gray-900">
            [Kế toán] Phiếu trả hàng
          </h1>
        </div>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách chứng từ nhập lại
            </h2>
          </div>
        </div>
        <div className="flex items-center justify-end mb-3">
          <button
            type="button"
            onClick={() => navigate(ROUTES.ACCOUNTING.PURCHASE_RETURN_CREATE)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4 text-green-700" />
            Thêm chứng từ
          </button>
        </div>

        <div className="mb-4 space-y-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Chi tiết hàng hoàn {selectedLabel ? `- ${selectedLabel}` : ""}
            </h3>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">STT</th>
                  <th className="px-3 py-2 text-left font-semibold">Kho</th>
                  <th className="px-3 py-2 text-left font-semibold">Tên hàng</th>
                  <th className="px-3 py-2 text-right font-semibold">Số lượng</th>
                  <th className="px-3 py-2 text-right font-semibold">Đơn giá hoàn</th>
                  <th className="px-3 py-2 text-right font-semibold">Thành tiền</th>
                  <th className="px-3 py-2 text-left font-semibold">Đơn vị</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                    {selectedItems.length ? (
                      selectedItems.map((item, index) => (
                        <tr key={`item-${index}`} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                          <td className="px-3 py-2 text-gray-900">
                        {(() => {
                          const code = wmsCodes.find(
                            (c) => String(c.id) === String((item as any).wms_id),
                          );
                          if (!code) return (item as any).wms_id ?? "";
                          const label = [code.code, code.name].filter(Boolean).join(" - ");
                          return label || code.id;
                        })()}
                          </td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.product_name ?? ""}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {parseNumber(item.quantity).toLocaleString("vi-VN")}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {parseNumber((item as any).return_price).toLocaleString("vi-VN")} ₫
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {parseNumber(item.return_amount).toLocaleString("vi-VN")} ₫
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {(() => {
                          const unit = units.find(
                            (u) => String(u.id) === String((item as any).unit_id),
                          );
                          if (!unit) return (item as any).unit_id ?? "";
                          // const label = [unit.name].filter(Boolean).join(" - ");
                          return unit.name || unit.id;
                        })()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      Chọn chứng từ trả hàng để xem chi tiết hàng hóa.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          defaultColDef={defaultColDef}
          totalPages={returnOrderData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          getRowId={(params) => String(params.data.id)}
          onQueryChange={handleQueryChange}
          onRowClicked={handleRowClick}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách chứng từ, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa chứng từ?"
        description={`Bạn chắc chắn muốn xóa chứng từ "${
          confirmRow?.sale_order_code ?? ""
        }"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteReturnOrder.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default ReturnOrders;
